import React from "react";
import { C } from "../../constants/design";
import {
  CardDef,
  CardFamily,
  CARDS,
  CARD_COST,
  FAMILIES,
  TIER_META,
} from "../../constants/cards";

/* ─── Icône cadenas (SVG inline, currentColor) ─────────────────────────────── */
export function LockIcon({
  open,
  size = 20,
}: {
  open: boolean;
  size?: number;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 49 49",
    fill: "none" as const,
    xmlns: "http://www.w3.org/2000/svg",
  };
  if (open) {
    return (
      <svg {...common}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M24.8742 1C29.3969 1 33.3756 3.32708 35.6795 6.84866C35.9755 7.30101 35.8065 7.89877 35.3384 8.16907L31.9139 10.1464C31.4266 10.4278 30.8079 10.2428 30.4743 9.78959C29.2093 8.07073 27.172 6.95545 24.8742 6.95545C21.0367 6.95545 17.9262 10.066 17.9262 13.9035V20.8515H11.9707V13.9035C11.9707 6.7771 17.7478 1 24.8742 1Z"
          fill="currentColor"
        />
        <path
          d="M39.7627 20.8516C40.8589 20.8517 41.748 21.7406 41.748 22.8369V36.4727C41.748 37.1723 41.3794 37.8207 40.7783 38.1787L26.9053 46.4414C25.6535 47.1868 24.0935 47.1869 22.8418 46.4414L8.96973 38.1787C8.36865 37.8207 8 37.1723 8 36.4727V22.8369C8 21.7405 8.88898 20.8516 9.98535 20.8516H39.7627ZM24.874 26.3105C22.6813 26.3105 20.9033 28.0885 20.9033 30.2812C20.9034 31.7505 21.7024 33.0322 22.8887 33.7188V35.7402C22.8887 36.8366 23.7777 37.7256 24.874 37.7256C25.9703 37.7255 26.8594 36.8366 26.8594 35.7402V33.7188C28.0456 33.0322 28.8437 31.7504 28.8438 30.2812C28.8438 28.0885 27.0667 26.3106 24.874 26.3105Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M31.8213 13.9035V20.8516H37.7768V13.9035C37.7768 6.77713 31.9997 1 24.8733 1C17.7469 1 11.9697 6.77713 11.9697 13.9035V20.8516H17.9252V13.9035C17.9252 10.066 21.0357 6.95548 24.8733 6.95548C28.7108 6.95548 31.8213 10.066 31.8213 13.9035Z"
        fill="currentColor"
      />
      <path
        d="M39.7627 20.8511C40.8589 20.8512 41.748 21.7401 41.748 22.8364V36.4722C41.748 37.1718 41.3794 37.8202 40.7783 38.1782L26.9053 46.4409C25.6535 47.1864 24.0935 47.1864 22.8418 46.4409L8.96875 38.1782C8.36781 37.8202 8 37.1717 8 36.4722V22.8364C8 21.7401 8.88898 20.8511 9.98535 20.8511H39.7627ZM24.875 26.311C22.6823 26.311 20.9044 28.0881 20.9043 30.2808C20.9043 31.7501 21.7032 33.0317 22.8896 33.7183V35.7397C22.8896 36.8361 23.7786 37.7251 24.875 37.7251C25.9712 37.7248 26.8594 36.836 26.8594 35.7397V33.7183C28.0458 33.0317 28.8447 31.7501 28.8447 30.2808C28.8446 28.0883 27.0675 26.3113 24.875 26.311Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ─── Pastille de rareté ───────────────────────────────────────────────────── */
export function RarityBadge({ card, sm }: { card: CardDef; sm?: boolean }) {
  const t = TIER_META[card.tier];
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: sm ? 8.5 : 10,
        fontWeight: 900,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "#06121f",
        background: t.color,
        borderRadius: 6,
        padding: sm ? "1px 5px" : "2px 8px",
        lineHeight: 1.3,
      }}
    >
      {t.label}
    </span>
  );
}

/* ─── Icônes de familles (boost) — cliquables ──────────────────────────────── */
export function FamilyDots({
  card,
  size = 22,
  onFamilyClick,
}: {
  card: CardDef;
  size?: number;
  onFamilyClick?: (fam: CardFamily) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {card.families.map((fam, i) => {
        const meta = FAMILIES[fam];
        const style: React.CSSProperties = {
          width: size,
          height: size,
          borderRadius: "50%",
          background: `${meta.color}30`,
          border: `1.5px solid ${meta.color}`,
          display: "grid",
          placeItems: "center",
          fontSize: size * 0.5,
          flexShrink: 0,
          padding: 0,
        };
        if (!onFamilyClick) {
          return (
            <span key={`${fam}-${i}`} title={meta.label} style={style}>
              {meta.icon}
            </span>
          );
        }
        return (
          <button
            key={`${fam}-${i}`}
            title={`Voir la famille ${meta.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onFamilyClick(fam);
            }}
            style={{ ...style, cursor: "pointer" }}
          >
            {meta.icon}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Carte de boutique (cadre = couleur de rareté) ────────────────────────── */
export function ShopCard({
  card,
  onInspect,
  onFamilyClick,
}: {
  card: CardDef;
  onInspect: (card: CardDef) => void;
  onFamilyClick?: (fam: CardFamily) => void;
}) {
  const rar = TIER_META[card.tier].color;
  const cost = CARD_COST[card.tier];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onInspect(card)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onInspect(card);
      }}
      style={{
        background: `linear-gradient(165deg,${rar}22,${C.surface})`,
        border: `1.5px solid ${rar}66`,
        borderRadius: 16,
        padding: "10px 10px 0",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: "pointer",
        textAlign: "left",
        minHeight: 124,
      }}
    >
      {/* Icône (centrée) */}
      <span
        style={{
          fontSize: 30,
          lineHeight: 1.4,
          alignSelf: "center",
          marginTop: 4,
        }}
      >
        {card.icon}
      </span>

      {/* Nom (centré) */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 900,
          color: C.text,
          lineHeight: 1.15,
          minHeight: 28,
          textAlign: "center",
        }}
      >
        {card.name}
      </span>

      {/* Combos (centrés sous le nom) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "4px 0 2px",
        }}
      >
        <FamilyDots card={card} size={18} onFamilyClick={onFamilyClick} />
      </div>

      {/* Séparateur */}
      <div
        style={{
          height: 1,
          background: "rgba(255,255,255,0.1)",
          margin: "4px -10px 0",
        }}
      />

      {/* Rareté (bas gauche) + prix (bas droite) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 0",
          gap: 4,
        }}
      >
        <RarityBadge card={card} sm />
        <span
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: C.text,
            whiteSpace: "nowrap",
          }}
        >
          💰 {cost}
        </span>
      </div>
    </div>
  );
}

/* ─── Emplacement vide (carte achetée) ─────────────────────────────────────── */
export function ShopCardPlaceholder() {
  return (
    <div
      style={{
        minHeight: 124,
        borderRadius: 16,
        background: "rgba(255,255,255,0.03)",
        border: "1.5px dashed rgba(255,255,255,0.1)",
        display: "grid",
        placeItems: "center",
        color: "rgba(255,255,255,0.18)",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      Acheté
    </div>
  );
}

/* ─── Bouton croix de fermeture ────────────────────────────────────────────── */
function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      onClick={onClose}
      aria-label="Fermer"
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 30,
        height: 30,
        borderRadius: 9,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        color: C.muted,
        fontSize: 16,
        fontWeight: 900,
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
        lineHeight: 1,
      }}
    >
      ✕
    </button>
  );
}

/* ─── Modale de détail d'une carte (+ Acheter si contexte boutique) ────────── */
export function CardModal({
  card,
  onClose,
  buy,
  sell,
}: {
  card: CardDef;
  onClose: () => void;
  buy?: { gold: number; onBuy: () => void; full?: boolean };
  sell?: { refund: number; onSell: () => void };
}) {
  const rar = TIER_META[card.tier].color;
  const cost = CARD_COST[card.tier];
  const canAfford = buy ? buy.gold >= cost : true;
  // "Golds insuffisants" est prioritaire sur "plus d'espace".
  const noSpace = !!buy?.full && canAfford;
  const buyDisabled = !canAfford || noSpace;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: C.surface,
          borderRadius: 22,
          border: `1.5px solid ${rar}66`,
          width: "100%",
          maxWidth: 360,
          padding: "24px 22px",
          textAlign: "center",
        }}
      >
        <CloseButton onClose={onClose} />
        <div
          style={{
            width: 80,
            height: 80,
            margin: "0 auto 14px",
            borderRadius: 18,
            background: `linear-gradient(160deg,${rar}40,${rar}10)`,
            border: `1.5px solid ${rar}`,
            display: "grid",
            placeItems: "center",
            fontSize: 40,
          }}
        >
          {card.icon}
        </div>
        <div
          style={{
            display: "flex",
            gap: 6,
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <RarityBadge card={card} />
          <FamilyDots card={card} size={20} />
        </div>
        <div style={{ fontSize: 19, fontWeight: 900 }}>{card.name}</div>
        <div
          style={{
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
            margin: "8px 0 16px",
          }}
        >
          {card.effect}
        </div>
        <div
          style={{
            fontSize: 11.5,
            lineHeight: 1.5,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 10,
            padding: "10px 12px",
            textAlign: "left",
            marginBottom: 16,
          }}
        >
          {card.families.map((f) => {
            const m = FAMILIES[f];
            return (
              <div key={f} style={{ marginBottom: 6 }}>
                <b style={{ color: m.color }}>
                  {m.icon} Combo {m.comboName}
                </b>
                <div style={{ marginTop: 2, color: C.muted }}>
                  2 cartes : {m.combo2}
                </div>
                <div style={{ marginTop: 2, color: C.muted }}>
                  4 cartes : {m.combo4}
                </div>
              </div>
            );
          })}
        </div>
        {buy ? (
          <button
            onClick={() => {
              buy.onBuy();
              onClose();
            }}
            disabled={buyDisabled}
            style={{
              width: "100%",
              background: !buyDisabled ? C.turf : "rgba(255,255,255,0.05)",
              color: !buyDisabled ? "#050f0a" : "rgba(255,255,255,0.25)",
              border: "none",
              borderRadius: 12,
              padding: "13px",
              fontSize: 15,
              fontWeight: 900,
              cursor: !buyDisabled ? "pointer" : "not-allowed",
            }}
          >
            {!canAfford
              ? `Not enough coins (💰 ${cost})`
              : noSpace
              ? "Inventory full (max 3)"
              : `Buy — 💰 ${cost}`}
          </button>
        ) : sell ? (
          <button
            onClick={() => {
              sell.onSell();
              onClose();
            }}
            style={{
              width: "100%",
              background: "rgba(255,80,80,0.12)",
              color: "#ff6b6b",
              border: "1px solid rgba(255,80,80,0.3)",
              borderRadius: 12,
              padding: "13px",
              fontSize: 15,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {sell.refund > 0 ? `Sell — 💰 ${sell.refund}` : "Sell (Common · Free)"}
          </button>
        ) : (
          <button
            onClick={onClose}
            style={{
              background: C.turf,
              color: "#050f0a",
              border: "none",
              borderRadius: 12,
              padding: "12px 28px",
              fontSize: 14,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Modale d'une famille (combo + cartes associées) ──────────────────────── */
export function FamilyModal({
  family,
  onClose,
}: {
  family: CardFamily;
  onClose: () => void;
}) {
  const m = FAMILIES[family];
  const cards = CARDS.filter((c) => c.families.includes(family));
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: C.surface,
          borderRadius: 22,
          border: `1.5px solid ${m.color}66`,
          width: "100%",
          maxWidth: 380,
          padding: "24px 22px",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <CloseButton onClose={onClose} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: `${m.color}30`,
              border: `1.5px solid ${m.color}`,
              display: "grid",
              placeItems: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            {m.icon}
          </span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: m.color }}>
              {m.label}
            </div>
            <div style={{ fontSize: 11.5, color: C.muted }}>{m.synergy}</div>
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            background: `${m.color}12`,
            border: `1px solid ${m.color}33`,
            borderRadius: 10,
            padding: "10px 12px",
            margin: "12px 0",
          }}
        >
          <b style={{ color: m.color }}>Combo {m.comboName}</b>
          <div style={{ marginTop: 4, color: C.muted }}>
            ● 2 cartes : {m.combo2}
          </div>
          <div style={{ marginTop: 3, color: C.muted }}>
            ●●●● 4 cartes : {m.combo4}
          </div>
        </div>

        <div
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            marginBottom: 8,
          }}
        >
          Cartes de la famille ({cards.length})
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {cards.map((c) => {
            const tier = TIER_META[c.tier];
            return (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${tier.color}40`,
                  borderRadius: 10,
                  padding: "8px 10px",
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `${tier.color}22`,
                    border: `1px solid ${tier.color}`,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 17,
                    flexShrink: 0,
                  }}
                >
                  {c.icon}
                </span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 800 }}>
                  {c.name}
                </span>
                <RarityBadge card={c} sm />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Bandeau récap des synergies d'équipe (type TFT) ──────────────────────── */
export function ComboRecap({
  combos,
}: {
  combos: {
    family: string;
    comboName: string;
    count: number;
    color: string;
    icon: string;
    tier2: boolean;
    tier4: boolean;
    effects: string[];
  }[];
}) {
  if (combos.length === 0) {
    return (
      <div
        style={{
          fontSize: 12,
          color: C.muted,
          background: C.surface,
          border: "1px dashed rgba(255,255,255,0.1)",
          borderRadius: 12,
          padding: "12px 14px",
          textAlign: "center",
        }}
      >
        Aucune synergie active. Envoyez des cartes de même famille sur le terrain.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {combos.map((c) => (
        <div
          key={c.family}
          style={{
            background: c.tier2 ? `${c.color}14` : "rgba(255,255,255,0.03)",
            border: `1px solid ${
              c.tier2 ? `${c.color}55` : "rgba(255,255,255,0.06)"
            }`,
            borderRadius: 12,
            padding: "9px 12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>{c.icon}</span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 900,
                color: c.tier2 ? c.color : C.muted,
                flex: 1,
              }}
            >
              {c.comboName}
            </span>
            <span style={{ display: "flex", gap: 5, alignItems: "center" }}>
              {[2, 4].map((thr) => {
                const on = c.count >= thr;
                return (
                  <span
                    key={thr}
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      fontFamily: "monospace",
                      color: on ? c.color : C.faint,
                      border: `1px solid ${on ? c.color : "rgba(255,255,255,0.12)"}`,
                      borderRadius: 6,
                      padding: "1px 5px",
                    }}
                  >
                    {thr}
                  </span>
                );
              })}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  fontFamily: "monospace",
                  color: c.tier2 ? c.color : C.muted,
                  marginLeft: 2,
                }}
              >
                {c.count}
              </span>
            </span>
          </div>
          {c.effects.map((e, i) => (
            <div
              key={i}
              style={{
                fontSize: 11,
                color: C.muted,
                lineHeight: 1.4,
                marginTop: 5,
              }}
            >
              • {e}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
